import { createDisplayNumberFormat } from '../utils/displayNumberFormat';
import '../styles/fitness-rate-glass.css';
import { NmLoader } from './NmLoader';
import { ActionButton } from './ui/ActionButton';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFitnessProgress } from '../hooks/useFitnessProgress';
import { fitnessProgress, fitnessSparkline, peakKind, recentPeakRows } from '../utils/fitnessProgress';
import { displayDigits } from '../utils/displayDigits';
import { formatApiDateCompact } from '../utils/calendarPickerLocale';
import '../styles/athlete-progress.css';

interface Props {
  athleteId?: number | null;
  refreshKey?: number;
  onOpenWorkout?: (id: number, date?: string, athleteId?: number | null) => void;
}
/** Shared desktop/mobile summary. Coach requests are lazy because expanded details mount this card. */
export function AthleteProgressCard({ athleteId, refreshKey = 0, onOpenWorkout }: Props) {
  const { t, i18n } = useTranslation();
  const [retry, setRetry] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const listId = useId();
  const result = useFitnessProgress(athleteId, refreshKey, retry);
  if (!athleteId) return null;
  const { windows, asOf } = fitnessProgress(result.days ?? [], result.today);
  const peaks = recentPeakRows(result.peaks ?? [], result.today);
  const number = (value: number, signed = false) => {
    const rounded = signed ? Math.round(value) : Math.round(value * 10) / 10;
    return displayDigits(`${signed && rounded > 0 ? '+' : ''}${createDisplayNumberFormat(i18n.language, { maximumFractionDigits: signed ? 0 : 1, useGrouping: false }).format(Object.is(rounded, -0) ? 0 : rounded)}`, i18n.language);
  };
  const failure = <p className="athlete-progress__empty">{t('fitnessProgress.error')} <ActionButton
        type="button"
        onClick={() => setRetry(value => value + 1)}
        wrap
      >{t('fitnessProgress.retry')}</ActionButton></p>;
  return <section className="athlete-progress" aria-label={t('fitnessProgress.label')} aria-busy={result.loading}>
    <div className="athlete-progress__grid">
      <div className="athlete-progress__ramp">
        <header><h3>{t('fitnessProgress.ramp')}</h3><details><summary aria-label={t('fitnessProgress.about')}>ⓘ</summary><p>{t('fitnessProgress.formula')}</p></details></header>
        {result.loading ? <NmLoader label={t('fitnessProgress.loading')} /> : result.rampError ? failure : <>
          <div className="athlete-progress__windows">{windows.map(window => <div className="fitness-rate-glass" key={window.days}>
            <span className="athlete-progress__caption">{t('fitnessProgress.days', { count: displayDigits(window.days) })}</span>
            <strong className={`athlete-progress__rate${window.rate != null && Math.round(window.rate) < 0 ? ' metric-value--negative' : ''}`}><bdi dir="ltr">{window.rate == null ? '—' : number(window.rate, true)}</bdi></strong>
            {window.rate != null && <svg viewBox="0 0 120 32" preserveAspectRatio="none" aria-hidden="true"><path d={fitnessSparkline(window.points)} /></svg>}
            <span className="athlete-progress__caption">{window.rate == null ? t('fitnessProgress.insufficient') : t('fitnessProgress.unit')}</span>
          </div>)}</div>
          {asOf && <p className="athlete-progress__asof">{t('fitnessProgress.asOf', { date: formatApiDateCompact(asOf, i18n.language) })}</p>}
        </>}
      </div>
      <div className="athlete-progress__records">
        <header><h3>{t('fitnessProgress.records')}</h3><span className="athlete-progress__caption">{t('fitnessProgress.recent')}</span></header>
        {result.loading ? <NmLoader label={t('fitnessProgress.loading')} /> : result.recordsError ? failure : !peaks.length ? <p className="athlete-progress__empty">{t('fitnessProgress.noRecords')}</p> : <>
          <div id={listId} className="athlete-progress__list">{(expanded ? peaks : peaks.slice(0, 3)).map(row => {
            const kind = peakKind(row.peak_type)!;
            const sportName = row.workout?.workout_type?.name ?? '';
            const sportKey = /bike|cycl|دوچرخه/i.test(sportName) ? 'bike' : /run|دویدن|دومیدانی/i.test(sportName) ? 'run' : /swim|شنا/i.test(sportName) ? 'swim' : /strength|قدرت/i.test(sportName) ? 'strength' : null;
            const sportLabel = sportKey ? t(`sportDuration.sports.${sportKey}`) : sportName;
            const workoutId = row.workout?.id ?? row.workout_id;
            const value = kind.metric === 'pace' ? displayDigits(`${Math.floor(Math.round(Number(row.value)) / 60)}:${String(Math.round(Number(row.value)) % 60).padStart(2, '0')}`) : number(Number(row.value));
            const content = <><span className="athlete-progress__record-label">{t(`fitnessProgress.metrics.${kind.metric}`)} · {displayDigits(kind.duration)} {t(`fitnessProgress.${kind.unit}`)}{sportLabel && <> · {displayDigits(sportLabel)}</>}</span><strong><bdi dir="ltr">{value}</bdi> <small>{t(`fitnessProgress.units.${kind.metric}`)}</small></strong><time dateTime={row.record_date.slice(0, 10)}>{formatApiDateCompact(row.record_date, i18n.language)}</time></>;
            return workoutId && onOpenWorkout ? <button type="button" className="athlete-progress__record" key={row.id} onClick={() => onOpenWorkout(workoutId, row.record_date.slice(0, 10), athleteId)}>{content}</button> : <div className="athlete-progress__record" key={row.id}>{content}</div>;
          })}</div>
          {peaks.length > 3 && <ActionButton
        type="button"
        className="athlete-progress__more"
        aria-expanded={expanded}
        aria-controls={listId}
        onClick={() => setExpanded(value => !value)}
        wrap
      >{t(expanded ? 'fitnessProgress.less' : 'fitnessProgress.more')}</ActionButton>}
        </>}
      </div>
    </div>
  </section>;
}
